
import { useState, useCallback, useRef, useEffect } from 'react';

export type LoadingPhase = 'initial' | 'refreshing' | 'stable';

interface LoadingState {
  phase: LoadingPhase;
  isLoading: boolean;
  hasLoadedOnce: boolean;
  lastStableTime: number; // Track when we last became stable
}

interface UseUnifiedLoadingStateReturn {
  loadingState: LoadingState;
  setInitialLoading: () => void;
  setRefreshing: () => void;
  setStable: () => void;
  shouldShowSkeleton: boolean;
  shouldShowRefreshIndicator: boolean;
}

const MINIMUM_STABLE_TIME = 300; // Minimum time to stay stable before allowing skeleton again
const DEBOUNCE_TIME = 100; // Debounce rapid state changes

export function useUnifiedLoadingState(): UseUnifiedLoadingStateReturn {
  const [loadingState, setLoadingState] = useState<LoadingState>({
    phase: 'initial',
    isLoading: true,
    hasLoadedOnce: false,
    lastStableTime: 0,
  });

  const debounceTimer = useRef<NodeJS.Timeout>();
  const stableTimer = useRef<NodeJS.Timeout>();

  const debouncedSetState = useCallback((newState: Partial<LoadingState>) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    
    debounceTimer.current = setTimeout(() => {
      setLoadingState(prev => ({ ...prev, ...newState }));
    }, DEBOUNCE_TIME);
  }, []);

  const setInitialLoading = useCallback(() => {
    // Only allow initial loading if we haven't loaded before
    if (!loadingState.hasLoadedOnce) {
      setLoadingState({
        phase: 'initial',
        isLoading: true,
        hasLoadedOnce: false,
        lastStableTime: 0,
      });
    }
  }, [loadingState.hasLoadedOnce]);

  const setRefreshing = useCallback(() => {
    const now = Date.now();
    const timeSinceStable = now - loadingState.lastStableTime;
    
    // Only allow refreshing if we've been stable long enough OR if we've never loaded
    if (loadingState.hasLoadedOnce && timeSinceStable > MINIMUM_STABLE_TIME) {
      debouncedSetState({
        phase: 'refreshing',
        isLoading: true,
      });
    } else if (!loadingState.hasLoadedOnce) {
      // Allow refreshing during initial load
      debouncedSetState({
        phase: 'refreshing',
        isLoading: true,
      });
    }
  }, [loadingState.hasLoadedOnce, loadingState.lastStableTime, debouncedSetState]);

  const setStable = useCallback(() => {
    const now = Date.now();
    
    // Clear any pending stable timer
    if (stableTimer.current) {
      clearTimeout(stableTimer.current);
    }
    
    // Add a small delay before setting stable to prevent rapid flicker
    stableTimer.current = setTimeout(() => {
      setLoadingState(prev => ({
        ...prev,
        phase: 'stable',
        isLoading: false,
        hasLoadedOnce: true,
        lastStableTime: now,
      }));
    }, 50); // Small delay to ensure smooth transition
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      if (stableTimer.current) {
        clearTimeout(stableTimer.current);
      }
    };
  }, []);

  // More conservative skeleton showing logic
  const shouldShowSkeleton = loadingState.phase === 'initial' && !loadingState.hasLoadedOnce;
  
  // Show refresh indicator only during refreshing phase, but not too frequently
  const shouldShowRefreshIndicator = loadingState.phase === 'refreshing' && loadingState.hasLoadedOnce;

  return {
    loadingState,
    setInitialLoading,
    setRefreshing,
    setStable,
    shouldShowSkeleton,
    shouldShowRefreshIndicator,
  };
}
